import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, X, ChevronsLeft } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { api, useServer } from '../contexts/ServerContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Link } from 'react-router-dom';
import { steamId64ToV2, normalizeSteamId } from '../utils/helpers';
import ScreengrabLightbox from './ScreengrabLightbox';
dayjs.extend(utc);
dayjs.extend(timezone);

function ObservationTab() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tiles, setTiles] = useState([]); // {steamId, displayName, image, loading}
  const [expanded, setExpanded] = useState(null); // steamId развернутого игрока
  const [screengrabHistory, setScreengrabHistory] = useState([]);
  const [screengrabLoading, setScreengrabLoading] = useState(false);
  const [screengrabPage, setScreengrabPage] = useState(1);
  const [screengrabTotalPages, setScreengrabTotalPages] = useState(1);
  const [selectedGrab, setSelectedGrab] = useState(null);
  const [lightboxGrab, setLightboxGrab] = useState(null);
  const screengrabLimit = 9;
  const { server } = useServer();

  useEffect(() => {
    fetchPlayers();
  }, [server]);

  useEffect(() => {
    if (players.length > 0) {
      // Загружаем скрины для всех онлайн-игроков
      Promise.all(players.map(async (player) => {
        const { image, createdAt } = await fetchLastScreengrab(player);
        return { steamId: player.steamId, displayName: player.displayName, image, createdAt, loading: false };
      })).then(setTiles);
    } else {
      setTiles([]);
    }
  }, [players]);

  // Загрузка истории скринграбов при открытии expanded
  useEffect(() => {
    if (!expanded) return;
    setScreengrabLoading(true);
    console.log('Loading screengrab history for:', expanded);
    api.get(`/api/admin/screengrab/history?steamId=${expanded}&limit=${screengrabLimit}&page=${screengrabPage}`)
      .then(res => {
        console.log('Screengrab history response:', res.data);
        setScreengrabHistory(res.data.history || []);
        setScreengrabTotalPages(res.data.totalPages || 1);
        if (res.data.history && res.data.history.length > 0) {
          console.log('Setting selected grab:', res.data.history[0]);
          setSelectedGrab(res.data.history[0]);
        } else {
          console.log('No history found, setting selectedGrab to null');
          setSelectedGrab(null);
        }
      })
      .catch((error) => {
        console.error('Error loading screengrab history:', error);
        setScreengrabHistory([]);
        setScreengrabTotalPages(1);
        setSelectedGrab(null);
      })
      .finally(() => setScreengrabLoading(false));
  }, [expanded, screengrabPage]);

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: 1,
        limit: 100,
        online: 'true',
        server: server
      });
      const response = await api.get(`/api/players?${params}`, { withCredentials: true });
      setPlayers(response.data.players);
    } catch (error) {
      toast.error('Ошибка при загрузке игроков');
    } finally {
      setLoading(false);
    }
  };

  const fetchLastScreengrab = async (player) => {
    try {
      console.log('Fetching last screengrab for player:', player.steamId);
      const res = await api.get(`/api/admin/screengrab/latest?steamId=${player.steamId}`, { withCredentials: true });
      console.log('Last screengrab response:', res.data);
      if (res.data && res.data.image) {
        console.log('Found image, length:', res.data.image.length);
        return { image: res.data.image, createdAt: res.data.createdAt };
      } else {
        console.log('No image found in response');
      }
    } catch (error) {
      console.error('Error fetching last screengrab:', error);
    }
    return { image: null, createdAt: null };
  };

  const handleScreengrab = async (player) => {
    setTiles(prev => prev.map(t => t.steamId === player.steamId ? { ...t, loading: true } : t));
    const key = uuidv4();
    const sid = normalizeSteamId(player.steamId);
    try {
      await api.post(`/api/admin/action?server=${server}`, {
        action: `screengrab"${sid}"${key}`,
        target: sid,
        reason: '',
        duration: 0
      }, { withCredentials: true });
      pollScreengrab(player, key);
    } catch (error) {
      toast.error('Ошибка при отправке скриншота');
      setTiles(prev => prev.map(t => t.steamId === player.steamId ? { ...t, loading: false } : t));
    }
  };

  const pollScreengrab = (player, key, attempts = 0) => {
    if (attempts > 45) {
      toast.error('Скриншот не получен');
      setTiles(prev => prev.map(t => t.steamId === player.steamId ? { ...t, loading: false } : t));
      return;
    }
    api.get(`/api/admin/screengrab?key=${key}`, { withCredentials: true })
      .then(async res => {
        if (res.data && res.data.image) {
          // После получения скрина — обновляем image и createdAt через fetchLastScreengrab
          const { image, createdAt } = await fetchLastScreengrab(player);
          setTiles(prev => prev.map(t => t.steamId === player.steamId ? { ...t, image, createdAt, loading: false } : t));
        } else {
          setTimeout(() => pollScreengrab(player, key, attempts + 1), 1000);
        }
      })
      .catch(() => {
        setTimeout(() => pollScreengrab(player, key, attempts + 1), 1000);
      });
  };

  // Layout: 5 в ряд, новые ряды по мере необходимости
  const getGridClass = (count) => {
    if (count <= 5) return `grid-cols-${count} grid-rows-1`;
    const rows = Math.ceil(count / 5);
    return `grid-cols-5 grid-rows-${rows}`;
  };

  // Высота панели зависит от количества рядов
  const getPanelHeight = (count) => {
    const rows = count <= 5 ? 1 : Math.ceil(count / 5);
    return 200 + rows * 260; // базовая + по 260px на ряд
  };

  return (
    <div className="flex items-center justify-center w-full border border-gmod-lighterer rounded-2xl" style={{ minHeight: '400px' }}>
      <div
        className={`bg-gmod-lighter rounded-2xl mx-auto my-auto flex items-center justify-center overflow-hidden p-6 ${expanded ? 'w-full' : 'w-full'}`}
        style={expanded
          ? { width: '100%', height: '100vh', minHeight: 0, minWidth: 0, maxHeight: 'none', transition: 'all 0.3s', margin: '0 auto' }
          : { minHeight: getPanelHeight(tiles.length), width: '100%', transition: 'min-height 0.3s', margin: '0 auto' }}
      >
        {loading ? (
          <div className="flex items-center justify-center w-full h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (!expanded && tiles.length === 0) ? (
          <div className="text-gmod-white opacity-60 m-auto">Нет онлайн-игроков</div>
        ) : expanded ? (
          // Развёрнутый режим
          <div className="relative w-full h-full flex flex-col flex-1">
            <div className="absolute top-4 left-4 z-20 flex flex-row gap-2">
              <button
                className="flex items-center justify-center p-2 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover"
                onClick={() => setExpanded(null)}
              >
                <ChevronsLeft className="h-5 w-5" />
              </button>
              {tiles.filter(t => t.steamId === expanded).map(tile => (
                <button
                  key={tile.steamId}
                  className="flex items-center justify-center p-2 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover"
                  onClick={() => handleScreengrab({ steamId: tile.steamId, displayName: tile.displayName })}
                  disabled={tile.loading}
                >
                  <RefreshCw className={`h-5 w-5 ${tile.loading ? 'animate-spin' : ''}`} />
                </button>
              ))}
            </div>
            <div className="flex-1 flex flex-col w-full h-full">
              {screengrabLoading ? (
                <div className="flex items-center justify-center w-full h-full">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
                </div>
              ) : selectedGrab ? (
                <div className="relative w-full h-full flex-1">
                  <div className="absolute top-0 left-0 w-full px-2 pt-2 pb-1 bg-black/60 z-10 text-center rounded-t-xl">
                    <Link
                      to={`/players/${expanded}`}
                      className="font-bold text-gmod-white text-xl truncate hover:text-theme-primary transition"
                      style={{ cursor: 'pointer' }}
                    >
                      {tiles.find(t => t.steamId === expanded)?.displayName}
                    </Link>
                    <div
                      className="text-gmod-white opacity-60 text-base truncate cursor-pointer hover:text-theme-primary"
                      title="Скопировать SteamID"
                      onClick={() => {navigator.clipboard.writeText(steamId64ToV2(expanded)); toast.success('SteamID скопирован!')}}
                    >
                      {expanded}
                    </div>
                    {selectedGrab.createdAt && (
                      <div className="text-gmod-white opacity-60 text-xs mt-1">Скрин: {dayjs(selectedGrab.createdAt).tz('Europe/Moscow').format('DD.MM.YYYY HH:mm:ss')}</div>
                    )}
                  </div>
                  <img
                    src={`data:image/jpeg;base64,${selectedGrab.image}`}
                    alt="Скриншот"
                    className="w-full h-full object-contain cursor-zoom-in"
                    onClick={() => setLightboxGrab(selectedGrab)}
                  />
                </div>
              ) : (
                <div className="text-gmod-white opacity-60 mb-2">Нет скриншотов</div>
              )}
              {/* Галерея превью */}
              {screengrabHistory.length > 0 && (
                <div className="flex flex-row gap-2 mt-2 overflow-x-auto px-2 pb-2">
                  {screengrabHistory.map((grab, idx) => (
                    <img
                      key={grab.key}
                      src={`data:image/jpeg;base64,${grab.image}`}
                      alt={`Скриншот ${idx + 1}`}
                      className={`h-20 w-auto rounded border-2 cursor-pointer ${selectedGrab && selectedGrab.key === grab.key ? 'border-theme-primary' : 'border-gmod-lighterer'} transition`}
                      onClick={() => setSelectedGrab(grab)}
                      title={dayjs(grab.createdAt).tz('Europe/Moscow').format('DD.MM.YYYY HH:mm:ss')}
                    />
                  ))}
                </div>
              )}
              {/* Пагинация */}
              {screengrabTotalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-2">
                  <button
                    onClick={() => setScreengrabPage(p => Math.max(1, p - 1))}
                    disabled={screengrabPage === 1}
                    className="px-3 py-1 bg-gmod-lighter text-gmod-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer text-sm"
                  >Назад</button>
                  <span className="text-gmod-white text-sm">Страница {screengrabPage} из {screengrabTotalPages}</span>
                  <button
                    onClick={() => setScreengrabPage(p => Math.min(screengrabTotalPages, p + 1))}
                    disabled={screengrabPage === screengrabTotalPages}
                    className="px-3 py-1 bg-gmod-lighter text-gmod-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer text-sm"
                  >Вперёд</button>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Плиточный режим
          <div className={`grid gap-4 w-full ${getGridClass(tiles.length)}`} style={{height: '100%'}}>
            {tiles.map((tile, idx) => (
              <div
                key={tile.steamId}
                className={`relative bg-gmod-dark border border-gmod-lighterer rounded-xl flex flex-col items-center justify-start transition-all duration-300 min-w-0 min-h-0 overflow-hidden cursor-pointer group`}
                style={{height: '260px', width: '100%'}}
                onClick={() => setExpanded(tile.steamId)}
              >
                <button
                  className="flex items-center justify-center p-2 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition"
                  onClick={e => { e.stopPropagation(); handleScreengrab({ steamId: tile.steamId, displayName: tile.displayName }); }}
                  disabled={tile.loading}
                >
                  <RefreshCw className={`h-4 w-4 ${tile.loading ? 'animate-spin' : ''}`} />
                </button>
                {tile.loading ? (
                  <div className="flex items-center justify-center w-full h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                ) : tile.image ? (
                  <img src={`data:image/jpeg;base64,${tile.image}`} alt="Скриншот" className="w-full h-full absolute inset-0 object-contain z-0" />
                ) : (
                  <div className="flex items-center justify-center w-full h-full">
                    <span className="text-gmod-white opacity-60 text-lg">Нет скриншота</span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 w-full px-2 pt-2 pb-1 bg-black/60 z-10 text-center rounded-b-xl">
                  <div className="font-bold text-gmod-white text-base truncate">{tile.displayName}</div>
                  <div className="text-gmod-white opacity-60 text-xs truncate">{tile.steamId}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {lightboxGrab && (
        <ScreengrabLightbox
          image={lightboxGrab.image}
          title={expanded ? tiles.find(t => t.steamId === expanded)?.displayName : undefined}
          subtitle={lightboxGrab.createdAt ? dayjs(lightboxGrab.createdAt).tz('Europe/Moscow').format('DD.MM.YYYY HH:mm:ss') : undefined}
          onClose={() => setLightboxGrab(null)}
        />
      )}
    </div>
  );
}

export default ObservationTab; 